import { BadgeStatus } from '@cas/components/data-display/badge-dot/BadgeDot';
import StatusCell from '@cas/components/data-display/status-cell/StatusCell';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { DeliveryStatus, DeviceMessageModel } from '@casops/store/apis/deviceMessagesApi';
import { ValueGetterFunc, ValueGetterParams } from 'ag-grid-community';
import i18n from '@casops/i18n';

interface DeviceMessageStatusCellRendererProps {
  value: DeliveryStatus;
  data: DeviceMessageModel;
}

export const deviceMessageStatusValueGetter = <TData extends { sts: DeliveryStatus }>({
  data,
}: ValueGetterParams<TData, string>): ReturnType<ValueGetterFunc<TData, string>> => {
  return typeof data?.sts === 'string' ? i18n.t(`DeviceMessageStatus.${data.sts}`) : null;
};

export function DeviceMessageStatusCellRenderer({
  value,
  data,
}: DeviceMessageStatusCellRendererProps): ReactElement | string {
  const { t } = useTranslation();

  let status = BadgeStatus.Healthy;

  switch (data.sts) {
    case DeliveryStatus.DeviceFailedToProcess:
    case DeliveryStatus.RejectedByCas:
    case DeliveryStatus.RejectedByDevice:
    case DeliveryStatus.UnsupportedByDevice:
    case DeliveryStatus.TimedOut:
      status = BadgeStatus.Danger;
      break;

    case DeliveryStatus.Unknown:
    case DeliveryStatus.WaitingForCasOps:
    case DeliveryStatus.WaitingForDevice:
    case DeliveryStatus.Processing:
      status = BadgeStatus.Information;
      break;

    case DeliveryStatus.Completed:
      status = BadgeStatus.Healthy;
      break;

    default:
      return <StatusCell label={t('Unknown')} status={BadgeStatus.Danger} />;
  }

  const label = t(`DeviceMessageStatus.${data.sts}`);

  return <StatusCell label={label} status={status} />;
}
